import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../provider/AppContext";
import PanelShell from "./PanelShell";
import {
  TYPE_META,
  TONE_GLYPH,
  GROUPS,
  matchesGroup,
  isUnread,
  searchText,
  shortWhen,
} from "./notificationMeta";
import moment from "moment";
import Notification from "@mui/icons-material/Notifications";
import GroupIcon from "@mui/icons-material/Group";
import Badge from "@mui/material/Badge";
import { useDispatch, useSelector } from "react-redux";
import {
  getNotificationUnreadCount,
  getNotificationsContent,
  handleNotificationAction,
  handleNotificationRead,
} from "../../redux/reducers/NotificationReducer";
import { useNavigate } from "react-router-dom";

const NotificationComponent = ({ showNotifications, setNotifications }) => {
  const navigate = useNavigate();
  const { fetchClientType, fetchEventType, user } = useContext(AppContext);
  const { notifications } = useSelector((state) => state.Notification);
  const dispatch = useDispatch();

  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("all");

  // Previously this fetched only unread notifications, so anything already read
  // vanished from the panel entirely. It now loads everything and filters
  // client-side, which is what makes an "Unread" tab meaningful.
  const refreshNotifications = () => {
    dispatch(getNotificationUnreadCount({ isSeen: false }));
    dispatch(getNotificationsContent({}));
  };

  useEffect(() => {
    user && refreshNotifications();
    // eslint-disable-next-line
  }, [user]);

  const close = () => setNotifications(false);

  /**
   * Each row reads as one sentence: who did what. The status ("Needs approval")
   * lives on the second line, so the sentence stays short enough to scan.
   */
  const describe = ({ eventId, data, eventComment }) => {
    const act = (type, id, accept) =>
      dispatch(handleNotificationAction({ type, eventId: id, accept }))
        .then(() => {
          refreshNotifications();
          fetchClientType();
          fetchEventType();
        })
        .catch((error) => console.error(error));

    const approveReject = (
      <div className="np-do">
        <button className="np-btn primary" onClick={() => act(eventId, data?.id, true)}>
          Approve
        </button>
        <button className="np-btn" onClick={() => act(eventId, data?.id, false)}>
          Reject
        </button>
      </div>
    );

    const named = <strong>{data?.label}</strong>;

    switch (eventId) {
      case 1:
        return { said: <>added client type {named}</>, actions: approveReject };
      case 2:
        return { said: <>added event type {named}</>, actions: approveReject };
      case 3:
        return { said: <>approved your client type</> };
      case 4:
        return { said: <>approved your event type</> };
      case 5:
        return {
          said: <>sent the contract for <strong>{data?.label || "an event"}</strong></>,
        };
      case 6:
        return { said: <>rejected your event type</> };
      case 7:
        return { said: <>rejected your client type</> };
      case 10:
        return {
          said: (
            <>
              changed the{" "}
              <strong>{data?.label || "contract"}</strong> template
            </>
          ),
          // Review opens the change in the diff view so the owner inspects it
          // before approving or rejecting — it never approves from here.
          actions: (
            <div className="np-do">
              <button
                className="np-btn primary"
                onClick={() => {
                  close();
                  navigate(`/template-contract/${eventComment}`);
                }}
              >
                Review &amp; approve
              </button>
            </div>
          ),
        };
      case 11:
        return {
          said: (
            <>
              approved your <strong>{data?.label || "template"}</strong> change
            </>
          ),
        };
      case 12:
        return {
          said: (
            <>
              rejected your <strong>{data?.label || "template"}</strong> change
            </>
          ),
        };
      case 13:
        return { said: <>added school {named}</>, actions: approveReject };
      case 14:
        return { said: <>approved your school</> };
      case 15:
        return { said: <>rejected your school</> };
      default:
        return { said: null };
    }
  };

  const all = useMemo(
    () =>
      (notifications || [])
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [notifications]
  );

  const counts = useMemo(() => {
    const c = {};
    GROUPS.forEach((g) => {
      c[g.key] = all.filter((n) => matchesGroup(n, g.key)).length;
    });
    return c;
  }, [all]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((n) => matchesGroup(n, group))
      .filter((n) => (q ? searchText(n).includes(q) : true));
  }, [all, group, query]);

  const sections = useMemo(() => {
    const buckets = new Map();
    visible.forEach((n) => {
      const d = moment(n.createdAt);
      const key = d.isSame(moment(), "day")
        ? "Today"
        : d.isSame(moment().subtract(1, "day"), "day")
        ? "Yesterday"
        : d.isAfter(moment().subtract(7, "day"))
        ? "This week"
        : "Earlier";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(n);
    });
    return [...buckets.entries()];
  }, [visible]);

  const tabs = GROUPS.map((g) => ({ ...g, n: counts[g.key] || 0 }));

  // Marking is explicit rather than "everything is read the moment you open
  // the panel", which is what made the Unread tab meaningless.
  const markAllRead = () => {
    dispatch(handleNotificationRead())
      .then(refreshNotifications)
      .catch(() => refreshNotifications());
  };

  return (
    <PanelShell
      open={showNotifications}
      onClose={close}
      title="Notifications"
      count={counts.unread || null}
      tabs={tabs}
      activeTab={group}
      onTab={setGroup}
      query={query}
      onQuery={setQuery}
      searchPlaceholder="Search notifications"
      action={
        counts.unread > 0 ? (
          <button className="np-text-btn" onClick={markAllRead}>
            Mark all read
          </button>
        ) : null
      }
    >
      {sections.length === 0 && (
        <div className="np-none">
          <b>{all.length === 0 ? "You're all caught up" : "No matches"}</b>
          {all.length === 0
            ? "New activity will show up here."
            : "Try a different tab or search."}
        </div>
      )}

      {sections.map(([label, items]) => (
        <React.Fragment key={label}>
          <div className="np-day">{label}</div>
          {items.map((n, i) => {
            const meta = TYPE_META[n.eventId];
            const { said, actions } = describe(n);
            const who =
              `${n.createdBy?.firstName || ""}`.trim() || "System";
            return (
              <div
                key={n.id ?? `${label}-${i}`}
                className={`np-row${isUnread(n) ? " is-unread" : ""}`}
              >
                <span className={`np-ico ${meta?.tone || "info"}`} aria-hidden="true">
                  {TONE_GLYPH[meta?.tone] || "•"}
                </span>
                <div className="np-main">
                  <div className="np-line1">
                    <span className="np-who">{who}</span>
                    <span className="np-said">{said}</span>
                    <span className="np-when">{shortWhen(n.createdAt)}</span>
                  </div>
                  {meta && (
                    <div className="np-line2">
                      <span className={`np-kind ${meta.tone}`}>{meta.kind}</span>
                    </div>
                  )}
                  {actions}
                </div>
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </PanelShell>
  );
};

export const NotificationBadge = ({ getNotifications, setNotifications }) => {
  const { count } = useSelector((state) => state.Notification);
  const dispatch = useDispatch();

  return (
    <div
      onClick={() => setNotifications(true)}
      className="notification-wrapper"
    >
      <Badge badgeContent={count || 0} color="primary" className="tw-font-xs">
        <Notification className="tw-text-2xl" color="white" />
      </Badge>
    </div>
  );
};

export const UserBadge = ({ activeUsersSummary, setActiveModal }) => {
  const userCount = useMemo(() => {
    return (activeUsersSummary || []).filter((user) => !user.logoutTime).length;
  }, [activeUsersSummary]);

  return (
    <div
      onClick={() => {
        setActiveModal(true);
      }}
      className="notification-wrapper active"
    >
      <Badge badgeContent={userCount} color="primary">
        <GroupIcon className="tw-text-2xl" color="white" />
      </Badge>
    </div>
  );
};

export default NotificationComponent;
